import { useEffect, useState } from 'react';
import Dexie from 'dexie';
import { deepReplace } from '../../utilities/offline_hooks_setup_apollo/offlineUtils';

const DB_VERSION = 2;
const DB_SCHEMA = {
  mutationQueues: '&userId',
  userInfo: '&userId, firstName, lastName, email',
};

export const useUserIndexedDB = (dbName) => {
  const [db, setDB] = useState(null);

  useEffect(() => {
    const database = new Dexie(dbName);

    database.version(DB_VERSION).stores(DB_SCHEMA);

    database
      .open()
      .then((dbInstance) => {
        setDB(dbInstance);
      })
      .catch((err) => {
        console.error('Failed to open db:', err);
      });

    return () => {
      if (database.isOpen()) database.close();
    };
  }, [dbName]);

  const isMutationDBOpen = () => {
    return db !== null && db.isOpen();
  };

  const ensureDBOpen = async () => {
    if (db && db.isOpen()) return db;

    // db is either null or was closed externally (SW versionchange event,
    // browser backgrounding the tab, or hook cleanup). Calling open() on a
    // closed Dexie instance is unreliable — create a fresh connection instead.
    const database = new Dexie(dbName);
    database.version(DB_VERSION).stores(DB_SCHEMA);
    try {
      const dbInstance = await database.open();
      setDB(dbInstance);
      return dbInstance;
    } catch (err) {
      console.error('Failed to open db:', err);
      return null;
    }
  };

  const errorString = (string) => {
    return `${string}: No Database Found`;
  };

  const addMutationToQueue = async (userId, mutation) => {
    const e = errorString('addMutationToQueue');
    const database = await ensureDBOpen();
    if (!database) {
      console.error(e);
      return;
    }
    let queueRecord = (await getQueue(userId)) || [];
    queueRecord.push(mutation);

    try {
      await database
        .table('mutationQueues')
        .put({ userId, queue: queueRecord });
    } catch (e) {
      console.error(e);
    }
  };

  const exchangeMutationQueue = async (userId, mutationQueue) => {
    const e = errorString('exchangeMutationQueue');
    const database = await ensureDBOpen();
    if (!database) {
      console.error(e);
      return;
    }
    await database
      .table('mutationQueues')
      .put({ userId, queue: mutationQueue });
  };

  const getQueue = async (userId) => {
    const e = errorString('getQueue');
    const database = await ensureDBOpen();
    if (!database) {
      console.error(e);
      return;
    }

    let queueRecord = await database.table('mutationQueues').get(userId);
    return queueRecord ? queueRecord.queue : null;
  };

  const dequeueMutation = async (userId) => {
    const e = errorString('dequeueMutation');
    const database = await ensureDBOpen();
    if (!database) {
      console.error(e);
      return;
    }

    let queueRecord = await getQueue(userId);
    if (!queueRecord || queueRecord.length === 0) {
      return null;
    }

    const item = queueRecord.shift();

    await exchangeMutationQueue(userId, queueRecord);

    return item;
  };

  const updateQueueWithDeepReplace = async (userId, oldValue, newValue) => {
    const e = errorString('updateQueueWithDeepReplace');
    const database = await ensureDBOpen();
    if (!database) {
      console.error(e);
      return;
    }

    let queueRecord = await getQueue(userId);
    if (!queueRecord) {
      return;
    }

    const updatedQueue = queueRecord.map((item) => {
      return deepReplace(item, oldValue, newValue);
    });

    await exchangeMutationQueue(userId, updatedQueue);
  };

  const clearMutationDatabase = async (userId) => {
    const e = errorString('clearMutationDatabase');
    const database = await ensureDBOpen();
    if (!database) {
      console.error(e);
      return;
    }
    await exchangeMutationQueue(userId, []);
  };

  const saveUserInfo = async (user) => {
    const e = errorString('saveUserInfo');
    const database = await ensureDBOpen();
    if (!database) {
      console.error(e);
      return;
    }

    if (!user) {
      console.error('no user object');
    }
    const result = await database.table('userInfo').put({
      userId: user?.id,
      firstName: user?.firstName,
      lastName: user.lastName,
    });

    return result;
  };
  const getQueueCount = async (userId) => {
    const e = errorString('getQueueCount');
    const database = await ensureDBOpen();
    if (!database) {
      console.error(e);
      return 0;
    }
  };

  return {
    getQueueCount,
    isMutationDBOpen,
    addMutationToQueue,
    exchangeMutationQueue,
    getQueue,
    dequeueMutation,
    updateQueueWithDeepReplace,
    clearMutationDatabase,
    saveUserInfo,
  };
};
